import {identifierFormFieldV2} from 'src/types/FormV2';
import type {IFieldType} from 'src/types/FieldOption';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

export const GetFieldValues = (
  type: string,
  data: IFieldType[]
): ISelectOption[] => {
  const values = data.filter(values => values.type === type)[0];

  if (values === undefined) {
    console.error('Unable to find type', type);
    return [];
  }

  return values.fields.map(field => {
    return {
      id: field.id,
      label: field.value,
    };
  });
};

// Text-backed currency fields validated by the currency regex.
const textCurrencyFieldLabels = {
  oshaCostText: 'Financial Cost',
  propertyDamageCost: 'Cost',
} as const;

// Number-backed currency fields rendered with the currency number input.
const numberCurrencyFieldLabels = {
  claimEstimatedTotalCost: 'Estimated Total Cost of Claim',
  claimFinanceAccrualsToDate: 'Finance Accruals to Date',
  claimTotalCostPaidByCompany: 'Total Cost Paid By Company',
  claimCostPaidByInsurance: 'Cost Paid By Insurance',
} as const;

const textCurrencyField = (
  name: keyof typeof textCurrencyFieldLabels
): IFormFieldV2 => ({
  name,
  label: textCurrencyFieldLabels[name],
  type: 'text',
  textboxOptions: {
    validationType: 'currency',
  },
});

const numberCurrencyField = (
  name: keyof typeof numberCurrencyFieldLabels
): IFormFieldV2 => ({
  name,
  label: numberCurrencyFieldLabels[name],
  type: 'number',
  numberOptions: {
    format: 'currency',
  },
});

// propertyDamageCost is int-backed, so it seeds as a number on edit which the
// currency text schema rejects; coerce it to a string at the load boundary.
export const coerceSafetyEventCurrencyValues = (
  data: IFormDataV2
): IFormDataV2 => {
  const result: IFormDataV2 = {...data};
  if (typeof result.propertyDamageCost === 'number') {
    result.propertyDamageCost = String(result.propertyDamageCost);
  }
  return result;
};

export const SafetyEventMeta = (
  data: IFieldType[],
  personnel?: ISelectOption[]
): IFormFieldV2[] => [
  identifierFormFieldV2,
  {
    name: 'heading',
    label: 'Event Overview',
    type: 'heading',
  },
  {
    name: 'incidentAt',
    label: 'Incident At',
    type: 'date',
    required: true,
  },
  {
    name: 'year',
    label: 'Incident Year',
    type: 'number',
    validation: {
      min: 1900,
      max: 2999,
    },
  },
  {
    name: 'quarter',
    label: 'Incident Quarter',
    type: 'number',
    validation: {
      min: 1,
      max: 4,
    },
  },
  {
    name: 'month',
    label: 'Incident Month',
    type: 'number',
    validation: {
      min: 1,
      max: 12,
    },
  },
  {
    name: 'day',
    label: 'Incident Day',
    type: 'number',
    validation: {
      min: 1,
      max: 31,
    },
  },
  {
    name: 'timePeriod',
    label: 'Time Period',
    type: 'select',
    required: true,
    options: GetFieldValues('TimePeriod', data),
  },
  {
    name: 'time',
    label: 'Time',
    type: 'text',
    required: true,
    textboxOptions: {
      validationType: 'time',
    },
  },
  {
    name: 'shift',
    label: 'Shift',
    type: 'select',
    required: true,
    options: GetFieldValues('Shift', data),
  },
  {
    name: 'jobDescription',
    label: 'Job Description',
    type: 'text',
    required: true,
    validation: {
      minLength: 3,
      maxLength: 500,
    },
  },
  {
    name: 'userType',
    label: 'User Type',
    type: 'select',
    required: true,
    options: GetFieldValues('UserType', data),
  },
  {
    name: 'eventPersonId',
    label: 'Event Person',
    type: 'autocomplete',
    required: true,
    options: personnel || [],
  },
  {
    name: 'location',
    label: 'Location',
    type: 'text',
    required: true,
  },
  {
    name: 'assetId',
    label: 'Related Asset',
    type: 'asset',
    description:
      'Choose an asset to relate to this safety event. Choosing an asset will link this safety event to that asset and cause it to appear on that asset profile.',
  },
  {
    name: 'projectId',
    label: 'Project',
    type: 'project',
    description:
      'Assign this safety event to a specific project, or leave as instance-wide.',
  },
  {
    name: 'safety',
    label: 'Safety',
    type: 'heading',
  },
  {
    name: 'safetyEventType',
    label: 'Event Type',
    type: 'select',
    options: GetFieldValues('SafetyEventType', data),
  },
  {
    name: 'safetyInjuryType',
    label: 'Injury Type',
    type: 'select',
    options: GetFieldValues('SafetyInjuryType', data),
  },
  {
    name: 'safetyBodyPart',
    label: 'Body Part',
    type: 'select',
    options: GetFieldValues('SafetyBodyPart', data),
  },
  {
    name: 'safetyBodySide',
    label: 'Body Side',
    type: 'select',
    options: GetFieldValues('SafetyBodySide', data),
  },
  {
    name: 'safetyFrontBack',
    label: 'Front/Back',
    type: 'select',
    options: GetFieldValues('SafetyFrontBack', data),
  },
  {
    name: 'safetyType',
    label: 'Safety Type',
    type: 'select',
    options: GetFieldValues('SafetyType', data),
  },
  {
    name: 'osha',
    label: 'OSHA',
    type: 'heading',
  },
  {
    name: 'oshaRecordable',
    label: 'Recordable',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'oshaGender',
    label: 'Gender',
    type: 'select',
    options: GetFieldValues('OshaGender', data),
  },
  {
    name: 'oshaInsuranceClaimText',
    label: 'Insurance Claim Text',
    type: 'text',
    validation: {
      maxLength: 500,
    },
  },
  textCurrencyField('oshaCostText'),
  {
    name: 'oshaInjuryType',
    label: 'Injury Type',
    type: 'select',
    options: GetFieldValues('OshaInjuryType', data),
  },
  {
    name: 'oshaLostTime',
    label: 'Lost Time',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'oshaLostTimeStartAt',
    label: 'Lost Time Start',
    type: 'date',
  },
  {
    name: 'oshaLostTimeEndAt',
    label: 'Lost Time End',
    type: 'date',
  },
  {
    name: 'oshaLostTimeDaysDiff',
    label: 'Lost Time Days',
    type: 'number',
  },
  {
    name: 'oshaRestrictionTime',
    label: 'Restriction',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'oshaRestrictionTimeStartAt',
    label: 'Restriction Start',
    type: 'date',
  },
  {
    name: 'oshaRestrictionTimeEndAt',
    label: 'Restriction End',
    type: 'date',
  },
  {
    name: 'oshaRestrictionTimeDaysDiff',
    label: 'Restriction Days',
    type: 'number',
  },
  {
    name: 'oshaFatality',
    label: 'Fatality',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'oshaTreatingDoctor',
    label: 'Treating Doctor',
    type: 'text',
    validation: {
      maxLength: 500,
    },
  },
  {
    name: 'oshaFacilityName',
    label: 'Facility Name',
    type: 'text',
    validation: {
      maxLength: 500,
    },
  },
  {
    name: 'oshaEmergencyRoomTreatment',
    label: 'Emergency Room Treatment',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'oshaHospitalizedOvernight',
    label: 'Hospitalized Overnight',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'environmental',
    label: 'Environmental',
    type: 'heading',
  },
  {
    name: 'environmentEventNature',
    label: 'Event Nature',
    type: 'select',
    options: GetFieldValues('EnvironmentEventNature', data),
  },
  {
    name: 'environmentSituation',
    label: 'Situation',
    type: 'select',
    options: GetFieldValues('EnvironmentSituation', data),
  },
  {
    name: 'environmentArea',
    label: 'Area',
    type: 'select',
    options: GetFieldValues('EnvironmentArea', data),
  },
  {
    name: 'environmentMaterial',
    label: 'Material',
    type: 'select',
    options: GetFieldValues('EnvironmentMaterial', data),
  },
  {
    name: 'environmentReleasePoint',
    label: 'Release Point',
    type: 'select',
    options: GetFieldValues('EnvironmentReleasePoint', data),
  },
  {
    name: 'environmentSeverity',
    label: 'Severity',
    type: 'select',
    options: GetFieldValues('EnvironmentSeverity', data),
  },
  {
    name: 'environmentRemediationDamage',
    label: 'Remediation Damage',
    type: 'select',
    options: GetFieldValues('EnvironmentRemediationDamage', data),
  },
  {
    name: 'environmentExpectedPenalties',
    label: 'Expected Penalties',
    type: 'select',
    options: GetFieldValues('EnvironmentExpectedPenalties', data),
  },
  {
    name: 'environmentImpact',
    label: 'Impact',
    type: 'select',
    options: GetFieldValues('EnvironmentImpact', data),
  },
  {
    name: 'environmentAmountSpilled',
    label: 'Amount Spilled',
    type: 'select',
    options: GetFieldValues('EnvironmentAmountSpilled', data),
  },
  {
    name: 'propertyDamage',
    label: 'Property Damage',
    type: 'heading',
  },
  {
    name: 'propertyDamageInterruption',
    label: 'Interruption',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  {
    name: 'propertyDamageWorkStopAt',
    label: 'Work Stop',
    type: 'date',
  },
  {
    name: 'propertyDamageWorkResumeAt',
    label: 'Work Resume',
    type: 'date',
  },
  textCurrencyField('propertyDamageCost'),
  {
    name: 'claimDetails',
    label: 'Claim Details',
    type: 'heading',
  },
  {
    name: 'claimCurrentIncidentStatus',
    label: 'Current Incident Status',
    type: 'textarea',
  },
  {
    name: 'claimInLitigation',
    label: 'In Litigation',
    type: 'switch',
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
  },
  numberCurrencyField('claimEstimatedTotalCost'),
  numberCurrencyField('claimFinanceAccrualsToDate'),
  {
    name: 'claimProjectedAccrualNotes',
    label: 'Projected Accrual Notes',
    type: 'textarea',
  },
  numberCurrencyField('claimTotalCostPaidByCompany'),
  numberCurrencyField('claimCostPaidByInsurance'),
  {
    name: 'claimFollowUpActivityDate',
    label: 'Follow-Up Activity Date',
    type: 'date',
  },
];

export const SafetyEventDocumentTitle = 'Safety Event';
